import _ from 'lodash';

import { handleAlarmStateSocketActions } from '@sophocles/entities/AlarmState';
import { handleCameraSocketActions } from '@sophocles/entities/Camera';
import { handleCameraInterfaceControllerSocketActions } from '@sophocles/entities/CameraInterfaceController';
import { handleCameraScheduleSocketActions } from '@sophocles/entities/CameraSchedule';
import { handleCompletedUnitCommandsSocketActions } from '@sophocles/entities/CompletedUnitCommand';
import { handleKoreActivationSocket } from '@sophocles/entities/KoreSubscription/actions';
import { handlePendingUnitCommandsSocketActions } from '@sophocles/entities/PendingUnitCommand';
import { handleRadioSocketActions } from '@sophocles/entities/Radio';
import { handleRelaySocketActions } from '@sophocles/entities/Relay';
import { handleRelayControllerStatusSocketActions } from '@sophocles/entities/RelayControllerStatus';
import { handleRelayControllerStatusHistorySocketActions } from '@sophocles/entities/RelayControllerStatusHistory';
import { handleRelayLoadSocketActions } from '@sophocles/entities/RelayLoad';
import { handleRelayLoadContractSocketActions } from '@sophocles/entities/RelayLoadContract';
import { handleRelayScheduleSocketActions } from '@sophocles/entities/RelaySchedule';
import { handleSiteSocketActions } from '@sophocles/entities/Site';
import { handleSiteAlarmOptionSocketActions } from '@sophocles/entities/SiteAlarmOption';
import { handleSiteConfigurationSocketActions } from '@sophocles/entities/SiteConfiguration';
import { handleSiteMaintenanceLogSocketActions } from '@sophocles/entities/SiteMaintenanceLog';
import { handleSmartlinkParameterActions } from '@sophocles/entities/SmartlinkParameter';
import {
  handleIntrusionSensorConfigurationActions,
  handleRelayControllerSocketActions,
  handleUpgradeProgressSocketActions,
} from '@sophocles/entities/SmartlinkUnit';
import { handleCalendarSocketActions } from '@sophocles/features/calendar/socketHandlers';
import {
  handleAlarmsSocketActions,
  handleCommandActivitySocketActions,
  handleUnitSocketActions,
  handleUpgradeProgressContractSocketActions,
} from '@sophocles/features/entities/site';
import {
  handleBinaryFileSocketActions,
  handleMediaSummaryContractSocketActions,
} from '@sophocles/features/media/slice';
import { handleSiteSensorsSocketActions } from '@sophocles/features/siteSensors/slice';
import { getDisplayForCommandName } from '@sophocles/utils';

const notifyNoAction = (op, data) => ({
  type: 'NOTIFY_NO_ACTION',
});

export function getModelHandler(model) {
  switch (model) {
    // New odl-app contract model names (dashboard slice).
    case 'unit':
      return handleUnitSocketActions;
    case 'alarms':
      return handleAlarmsSocketActions;
    case 'media_summary':
      return handleMediaSummaryContractSocketActions;
    case 'command_activity':
      return handleCommandActivitySocketActions;
    case 'upgrade_progress':
      return handleUpgradeProgressContractSocketActions;
    case 'calendar':
      return handleCalendarSocketActions;
    case 'binaryfile':
      return handleBinaryFileSocketActions;
    // Legacy site_sensors wire name kept for non-dashboard consumers.
    case 'site_sensors':
      return handleSiteSensorsSocketActions;
    case 'alarmstate':
      return handleAlarmStateSocketActions;
    case 'camera':
      return handleCameraSocketActions;
    case 'camerainterfacecontroller':
      return handleCameraInterfaceControllerSocketActions;
    case 'cameraschedule':
      return handleCameraScheduleSocketActions;
    case 'completedunitcommand':
      return handleCompletedUnitCommandsSocketActions;
    case 'pendingunitcommand':
      return handlePendingUnitCommandsSocketActions;
    case 'radio':
      return handleRadioSocketActions;
    case 'radio_activation':
      return handleKoreActivationSocket;
    case 'relay':
      return handleRelaySocketActions;
    case 'relaycontroller':
      return handleRelayControllerSocketActions;
    case 'relaycontrollerparameters':
      return handleSmartlinkParameterActions;
    case 'relaycontrollerstatus':
      return handleRelayControllerStatusSocketActions;
    case 'relaycontrollerstatushistory':
      return handleRelayControllerStatusHistorySocketActions;
    case 'relayload':
      return handleRelayLoadSocketActions;
    case 'relayloadcontract':
      return handleRelayLoadContractSocketActions;
    case 'relayschedule':
      return handleRelayScheduleSocketActions;
    case 'site':
      return handleSiteSocketActions;
    case 'sitealarmoption':
      return handleSiteAlarmOptionSocketActions;
    case 'siteconfiguration':
      return handleSiteConfigurationSocketActions;
    case 'sitemaintenancelog':
      return handleSiteMaintenanceLogSocketActions;
    case 'intrusionsensorconfiguration':
      return handleIntrusionSensorConfigurationActions;
    case 'sl2intrusionsensorconfiguration':
      // The dashboard consumes the normalized unit contract emitted with this event.
      return notifyNoAction;
    case 'upgradeprogress':
      return handleUpgradeProgressSocketActions;
    default:
      console.log('no socket handler for model:', model);
      return notifyNoAction;
  }
}

function replaceCommandInText(text, command) {
  const commandDisplay = getDisplayForCommandName(command);
  return _.replace(text, command, commandDisplay);
}

export function createSocketToast(data, t = null) {
  if (data?.message == null) {
    return null;
  }

  let title = data.message.title;
  let text = data.message.text;
  const level = data.message.level ?? 'info';
  const command = data.message.command;
  const localeData = data.locale_data;

  if (localeData && t != null) {
    const { key, subs } = localeData;
    const subObj = Object.fromEntries(
      Object.entries(subs).map(([k, v]) => {
        const sub = t(v.key);
        return [k, sub && sub !== v.key ? sub : v.default];
      }),
    );
    // i18next returns the key itself for missing translations, not
    // null/undefined — so `?? text` never falls back. Compare against
    // the key to detect a missing translation and keep the
    // backend-translated `text` (built from the Locale table by
    // ``translate()`` in ``pliny/utils/locale.py``).
    const translated = t(key, subObj);
    if (translated && translated !== key) {
      text = translated;
    }
  } else if (command) {
    // handle commands
    text = replaceCommandInText(text, command);
  }

  // handle empty titles
  if (title == null && level == 'success') {
    title = 'Success';
  } else if (title == null && level == 'danger') {
    title = 'Oh No!';
  }

  return text == null && title == null ? null : { message: text, level: level, title: title };
}
